import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Link,
} from '@react-pdf/renderer';
import logo from '../../images/57logo.jpeg';
import {
  calculateWorkingHours,
  formatDate,
  formatDateTimeAuto,
} from '../Utils/Utils';

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 9,
  },

  /* ---------- HEADER ---------- */
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderBottomColor: '#112131',
    paddingBottom: 6,
    marginBottom: 10,
  },

  title: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#112131',
  },

  subtitle: {
    fontSize: 9,
    color: '#555',
  },

  logo: {
    width: 80,
    height: 55,
  },

  /* ---------- INFO BOX ---------- */
  infoBox: {
    marginBottom: 10,
    padding: 8,
    backgroundColor: '#f6f7f9',
    borderRadius: 4,
  },

  infoRow: {
    flexDirection: 'row',
    marginBottom: 3,
  },

  infoLabel: {
    width: 80,
    fontWeight: 'bold',
  },

  /* ---------- TABLE ---------- */
  table: {
    borderWidth: 1,
    borderColor: '#ccc',
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#112131',
  },

  headerCell: {
    padding: 4,
    color: '#fff',
    fontWeight: 'bold',
    borderRightWidth: 1,
    borderRightColor: '#fff',
    textAlign: 'center',
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    minHeight: 26,
  },

  rowAlt: {
    backgroundColor: '#f8f9fb',
  },

  cell: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
    wordBreak: 'break-word',
  },

  center: {
    textAlign: 'center',
  },

  /* ---------- FOOTER ---------- */
  footer: {
    position: 'absolute',
    bottom: 12,
    right: 20,
    fontSize: 9,
    color: '#666',
  },
});

/* ================= COLUMN WIDTHS ================= */

const COL = {
  index: 30,
  staffId: 55,
  name: 125,        // ⬆ increased
  nric: 60,
  grade: 110,        // ⬆ increased
  duty: 70,
  site: 95,
  time: 65,
  hrs: 50,
  location: 45,    // ⬇ reduced (only "View")
};

/* ================= COMPONENT ================= */

const AttendanceReportPDF = ({ data, sites, employeeId, site_id, from, to }) => {
  const currentDate = new Date().toLocaleDateString('en-GB');
  const siteDetails = site_id ? sites.find(s => s.id === site_id) : null;
  const address = siteDetails?.address?.[0];
  let locationWidth = 45;

  if (employeeId && site_id) {
    locationWidth = 212; // (45+45)+335/2
  } else if (employeeId) {
    locationWidth = 165; // (45+45)+240/2
  } else if (site_id) {
    locationWidth = 92; // (45+45)+95/2
  }
  console.log("siteId", site_id)
console.log("siteDetails", siteDetails)
  console.log("sites", sites);
  console.log("data", data);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>

        {/* ================= HEADER (REPEATS) ================= */}
        <View style={styles.header} fixed>
          <View>
            <Text style={styles.title}>Attendance Report</Text>
            <Text style={styles.subtitle}>Generated on: {currentDate}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Image src={logo} style={styles.logo} />
            <Text style={styles.subtitle}>57 Peace Protection</Text>
          </View>
        </View>

        {/* ================= INFO SECTION ================= */}
        <View style={styles.infoBox}>

          {/* -------- SITE DETAILS -------- */}
          {site_id && siteDetails && (
            <>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Site Name:</Text>
                <Text>{siteDetails.name || 'N/A'}</Text>
              </View>

              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Address:</Text>
                {/* <Text>
                  {address
                    ? `${address.unitNo || ''}, ${address.street || ''}, ${address.city || ''}, ${address.country || ''}.`
                    : '-'}
                </Text> */}

                <Text>
                  {address
                    ? [
                      address.unitNo,
                      address.street,
                      address.city,
                      [address.country, address.pincode]
                        .filter(Boolean)
                        .join(" - "),
                    ]
                      .filter(Boolean)
                      .join(", ")
                    : "-"}
                </Text>
              </View>
            </>
          )}

          {/* -------- USER DETAILS -------- */}
          {employeeId && (
            <>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Staff Name:</Text>
                <Text>
                  {data[0]?.userDetails
                    ? `${data[0].userDetails.firstName || ''} ${data[0].userDetails.lastName || ''}`.trim()
                    : 'N/A'}
                </Text>
              </View>

              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Role:</Text>
                <Text>{data[0]?.roleDetails?.[0]?.name || 'N/A'}</Text>
              </View>

              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>NRIC OR FIN :</Text>
                <Text>{data[0]?.userDetails.nricOrFin || 'N/A'}</Text>
              </View>
            </>
          )}

          {/* -------- DATE RANGE (ALWAYS SHOWN) -------- */}
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>From:</Text>
            <Text>{formatDate(from)}</Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>To:</Text>
            <Text>{formatDate(to)}</Text>
          </View>

        </View>


        {/* ================= TABLE HEADER (REPEATS) ================= */}
        <View style={styles.tableHeader} fixed>
          <Text style={[styles.headerCell, { width: COL.index }]}>#</Text>

          {!employeeId && (
            <>
              <Text style={[styles.headerCell, { width: COL.staffId }]}>Staff ID</Text>
              <Text style={[styles.headerCell, { width: COL.name }]}>Name</Text>
              <Text style={[styles.headerCell, { width: COL.nric }]}>NRIC</Text>
            </>
          )}

          <Text style={[styles.headerCell, { width: COL.grade }]}>Grade</Text>
          <Text style={[styles.headerCell, { width: COL.duty }]}>Duty</Text>

          {!site_id && (
            <Text style={[styles.headerCell, { width: COL.site }]}>Site</Text>
          )}

          <Text style={[styles.headerCell, { width: COL.time }]}>In</Text>
          <Text style={[styles.headerCell, { width: COL.time }]}>Out</Text>
          <Text style={[styles.headerCell, { width: COL.hrs }]}>Hrs</Text>
          <Text style={[styles.headerCell, { width: locationWidth }]}>In Loc</Text>
          <Text style={[styles.headerCell, { width: locationWidth }]}>Out Loc</Text>
        </View>

        {/* ================= TABLE BODY ================= */}
        <View style={styles.table}>
          {data.map((d, i) => (
            <View
              key={i}
              style={[styles.tableRow, i % 2 === 0 && styles.rowAlt]}
            >
              <Text style={[styles.cell, styles.center, { width: COL.index }]}>{i + 1}</Text>

              {/* {!user_id && (
                <>
                  <Text style={[styles.cell, { width: COL.staffId }]}>{d.userDetails?.employeeId}</Text>
                  <Text style={[styles.cell, { width: COL.name }]}>
                    { d.userDetails?.firstName ? d.userDetails?.firstName d.userDetails?.lastName : d.staffName}
                  </Text>
                </>
              )} */}

              {!employeeId && (
                <>
                  <Text style={[styles.cell, { width: COL.staffId }]}>
                    {d.userDetails?.employeeId ? d.userDetails?.employeeId : '#'}
                  </Text>

                  <Text style={[styles.cell, { width: COL.name }]}>
                    {d.userDetails?.firstName
                      ? `${d.userDetails?.firstName} ${d.userDetails?.lastName || ""}`
                      : d.staffName}
                  </Text>
                  <Text style={[styles.cell, { width: COL.nric }]}>
                    {d.userDetails?.nricOrFin ? d.userDetails?.nricOrFin : d.nricOrFin}
                  </Text>
                </>
              )}



              <Text style={[styles.cell, { width: COL.grade }]}>
                {d.roleDetails?.[0]?.name || '-'}
              </Text>

              <Text style={[styles.cell, { width: COL.duty }]}>
                {d.dutyType || '-'}
              </Text>

              {!site_id && (
                <Text style={[styles.cell, { width: COL.site }]}>
                  {d.siteDetails[0]?.name || '-'}
                </Text>
              )}

              <Text style={[styles.cell, { width: COL.time }]}>
                {d.entryTime ? formatDateTimeAuto(d.entryTime) : '-'}
              </Text>

              <Text style={[styles.cell, { width: COL.time }]}>
                {d.exitTime ? formatDateTimeAuto(d.exitTime) : '-'}
              </Text>

              <Text style={[styles.cell, styles.center, { width: COL.hrs }]}>
                {d.entryTime && d.exitTime
                  ? calculateWorkingHours(d.entryTime, d.exitTime)
                  : '-'}
              </Text>

              <Text style={[styles.cell, { width: locationWidth }]}>
                {d.entryMapUrl ? <Link src={d.entryMapUrl}>View</Link> : '-'}
              </Text>

              <Text style={[styles.cell, { width: locationWidth }]}>
                {d.exitMapUrl ? <Link src={d.exitMapUrl}>View</Link> : '-'}
              </Text>
            </View>
          ))}
        </View>

        {/* ================= FOOTER WITH PAGE NUMBER ================= */}
        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} / ${totalPages}`
          }
          fixed
        />

      </Page>
    </Document>
  );
};

export default AttendanceReportPDF;

